// components/MapScreen/RestaurantMarker.web.js
import React, { memo, useMemo, useCallback } from 'react';
import { AdvancedMarker } from '@vis.gl/react-google-maps';
import { PALOMA_NAVY } from '../../constants/palomaPaloozaBrand';

const RestaurantMarker = memo(({
  restaurant,
  isSelected,
  onPress,
  hasCheckedIn,
  position,
  speedRailzPromoEnabled = false,
  heavenHillsPromoEnabled = false,
}) => {
  // Color logic matching native version exactly:
  // - Blue (#3B82F6) when selected
  // - Yellow (#FBBF24) for SpeedRailz partners when promo flag is on
  // - Navy for Heaven Hills / Paloma when promo flag is on
  // - #5E3D2D (darker brown) if user has checked in before
  // - #9C6B42 (brown) for Disgo partners
  // - #A1A1A1 (light grey) for non-partners
  let markerColor;
  let badgeColor;
  if (isSelected) {
    markerColor = "#3B82F6"; // Blue for selected restaurant
    badgeColor = "#3B82F6"; // Match marker color for selected
  } else if (speedRailzPromoEnabled && restaurant?.isSpeedRailzPartner) {
    markerColor = "#FBBF24"; // Yellow for SpeedRailz partners
    badgeColor = "#FBBF24"; // Match marker color for SpeedRailz partners
  } else if (heavenHillsPromoEnabled && restaurant?.isHeavenHills) {
    markerColor = PALOMA_NAVY;
    badgeColor = PALOMA_NAVY;
  } else if (hasCheckedIn) {
    markerColor = "#5E3D2D"; // Darker brown for visited restaurants
    badgeColor = "#5E3D2D"; // Match marker color for checked in partners
  } else if (restaurant?.isPartner) {
    markerColor = "#9C6B42"; // Brown for Disgo partners
    badgeColor = "#9C6B42"; // Match marker color for non-checked in partners
  } else {
    markerColor = "#A1A1A1"; // Light grey for non-partners
    badgeColor = "#A1A1A1"; // Match marker color for non-partners
  }

  const PIN_SIZE_DEFAULT = 44;
  const PIN_SIZE_SELECTED = 58;
  const pinSize = isSelected ? PIN_SIZE_SELECTED : PIN_SIZE_DEFAULT;
  const scale = pinSize / PIN_SIZE_DEFAULT;

  const badgeSize = isSelected ? Math.round(24 * scale) : 20;
  const badgeOffset = (pinSize - badgeSize) / 2;
  const badgeTop = badgeOffset - Math.round(4 * scale);
  const giftIconSize = isSelected ? Math.round(16 * scale) : 14;

  // Create marker content using HTML/CSS for AdvancedMarker
  // Using the exact Entypo location-pin shape (teardrop/waterdrop shape)
  const markerContent = useMemo(() => {
    return (
      <div
        style={{
          width: `${pinSize}px`,
          height: `${pinSize}px`,
          position: 'relative',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: isSelected ? 1000 : 1,
        }}
      >
        {/* Location pin SVG - matching Entypo location-pin shape exactly */}
        <svg
          width={pinSize}
          height={pinSize}
          viewBox="0 0 24 24"
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
          }}
          xmlns="http://www.w3.org/2000/svg"
        >
          {/* Entypo location-pin shape - teardrop with point at bottom */}
          <path
            d="M12 0C7.589 0 4 3.589 4 8c0 4.541 7.105 15.953 7.105 15.953a.998.998 0 0 0 1.79 0C12.895 23.953 20 12.541 20 8c0-4.411-3.589-8-8-8zm0 11c-1.657 0-3-1.343-3-3s1.343-3 3-3 3 1.343 3 3-1.343 3-3 3z"
            fill={markerColor}
          />
        </svg>
        
        {/* Gift badge overlay for partners - matching native exactly */}
        {restaurant?.isPartner && (
          <div
            style={{
              position: 'absolute',
              top: `${badgeTop}px`,
              left: `${badgeOffset}px`,
              width: `${badgeSize}px`,
              height: `${badgeSize}px`,
              backgroundColor: badgeColor,
              borderRadius: `${badgeSize / 2}px`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 1,
            }}
          >
            {/* Gift icon SVG - matching AntDesign gift icon */}
            <svg
              width={giftIconSize}
              height={giftIconSize}
              viewBox="0 0 1024 1024"
              fill="white"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path d="M880 310H732.4c13.6-21.4 21.6-46.8 21.6-74 0-76.1-61.9-138-138-138-41.4 0-78.7 18.4-104 47.4-25.3-29-62.6-47.4-104-47.4-76.1 0-138 61.9-138 138 0 27.2 8 52.6 21.6 74H144c-17.7 0-32 14.3-32 32v432c0 17.7 14.3 32 32 32h736c17.7 0 32-14.3 32-32V342c0-17.7-14.3-32-32-32zM513.1 245.7c23.1 0 41.9-18.8 41.9-41.9 0-23.1-18.8-41.9-41.9-41.9s-41.9 18.8-41.9 41.9c0 23.1 18.8 41.9 41.9 41.9zm-185.2 0c23.1 0 41.9-18.8 41.9-41.9 0-23.1-18.8-41.9-41.9-41.9s-41.9 18.8-41.9 41.9c0 23.1 18.8 41.9 41.9 41.9zM840 770H184V374h176v104c0 4.4 3.6 8 8 8h56c4.4 0 8-3.6 8-8V374h152v104c0 4.4 3.6 8 8 8h56c4.4 0 8-3.6 8-8V374h176v396z" />
            </svg>
          </div>
        )}
      </div>
    );
  }, [markerColor, badgeColor, restaurant?.isPartner, badgeSize, badgeOffset, badgeTop, giftIconSize, pinSize, isSelected]);

  if (!position?.lat || !position?.lng) {
    return null;
  }

  const handleClick = useCallback((e) => {
    // Prevent event from causing map bounds updates
    if (e) {
      e.stopPropagation?.();
      e.preventDefault?.();
    }
    // Call immediately without deferring - the parent handles debouncing
    if (onPress) {
      onPress();
    }
  }, [onPress]);

  return (
    <AdvancedMarker
      position={position}
      title={restaurant?.name}
      onClick={handleClick}
    >
      {markerContent}
    </AdvancedMarker>
  );
});

RestaurantMarker.displayName = 'RestaurantMarker';

export default RestaurantMarker;

